import 'lib-js-password/lib/passwordValidator';
import { PasswordValidator } from '../helpers/PasswordValidator';
import { intervalGetPropagation } from '../conf/password.conf';

import ApiService from '../services/ApiService';
import * as types from './ActionTypes';

import {
  goToErrorPage, goToNextStep, setInputError,
} from './GenericActions';
import { recordingProbeStarted } from './probes_action';
import TagService from '../services/TagService';

/**
 * Initialize the passwordValidator and the passwordRating library
 * @returns {{type: string, receivedAt: number}}
 */
export const initPasswordValidator = () => {
  window.passwordValidator.init(PasswordValidator.getConf());
  return {
    type: types.INIT_PASSWORD_RULES,
  };
};

/**
 * Check if the password respect the CNIL rules with the passwordValidator library
 * @param {string} password             new password input
 * @returns {object} passwordStatuses   password statuses result
 */
export const updateCnil = (password) => {
  const rules = window.passwordValidator.validate(password);

  const errors = {
    accentedChars: rules.accentedChars,
    space: rules.space,
    specialChars: rules.specialChars,
    max: rules.max,
  };
  return {
    type: types.UPDATE_CNIL,
    payload: {
      rules,
      errors,
      password,
    },
  };
};

/**
 * Reset the password succeeded
 * @returns {{type: string, receivedAt: number}}
 */
const resetPasswordSuccess = () => ({
  type: types.UPDATE_PASSWORD_SUCCESS,
  receivedAt: Date.now(),
});

/**
 * Reset the password is pending with the partners
 * @returns {{type: string, receivedAt: number}}
 */
const resetPasswordPending = () => ({
  type: types.UPDATE_PASSWORD_PENDING,
  receivedAt: Date.now(),
});

const updatePasswordAvailableSoon = () => ({
  type: 'UPDATE_PASSWORD_AVAILABLE_SOON',
});

/**
 * Reset the password failed
 * @returns {{type: string, receivedAt: number}}
 */
const resetPasswordFailure = error => ({
  type: types.UPDATE_PASSWORD_FAILURE,
  error,
  receivedAt: Date.now(),
});

/**
 * Reset the password failed
 * @returns {{type: string, receivedAt: number}}
 */
const checkPropagationPartners = () => ({
  type: types.CHECK_PROPAGATION_PARTNERS,
  receivedAt: Date.now(),
});

export const setCnilError = error => ({
  type: types.SET_CNIL_ERROR,
  payload: {
    error,
  },
});

/**
 * Check if the update's propagation of the password in other partners is finished or not yet.
 * @param identitiesId
 * @return {const}
 */
export function doPropagationCheck({ identitiesId }) {
  return (dispatch) => {
    dispatch(checkPropagationPartners());
    return ApiService.checkPropagationPartnersPassword({ identitiesId })
      .then(
        (response) => {
          switch (response.status) {
            case 200:
              if (response.data && response.data.code === 408001) {
                dispatch(updatePasswordAvailableSoon());
                dispatch(goToErrorPage('resetIsPending'));
              } else {
                dispatch(resetPasswordSuccess());
                dispatch(goToNextStep(response.data.step));
              }
              break;
            case 204:
              setTimeout(() => dispatch(doPropagationCheck({ identitiesId })), intervalGetPropagation);
              break;
            default:
              break;
          }
        },
        (err) => {
          dispatch(resetPasswordFailure(err.response));
          if (err.response) {
            if (err.response.status === 408) {
              dispatch(updatePasswordAvailableSoon());
              dispatch(goToErrorPage('resetIsPending'));
            } else {
              dispatch(goToErrorPage());
            }
          }
        },
      );
  };
}

/**
 * Update the password of the current user with the new one.
 * If the update is validated, push to /confirm or /contact
 * @param identitiesId
 * @param password
 * @param passAgain
 * @return {const}
 */
const doResetPassword = ({ identitiesId, password, passAgain }) => ((dispatch) => {
  dispatch(recordingProbeStarted());
  dispatch(resetPasswordPending());
  return ApiService.patchPassword({ identitiesId, password, passAgain })
    .then(
      (res) => {
        switch (res.status) {
          case 200: {
            const { step } = res.data;
            dispatch(resetPasswordSuccess());
            dispatch(goToNextStep(step));
            break;
          }
          case 204:
            setTimeout(() => dispatch(doPropagationCheck({ identitiesId })), intervalGetPropagation);
            break;
          default:
            break;
        }
      },
      (err) => {
        dispatch(resetPasswordFailure(err.response));
        if (err.response && err.response.data) {
          const { code, field } = err.response.data;
          switch (field) {
            case 'password':
              switch (code) {
                case 412002:
                  dispatch(setInputError({ field, error: 'empty' }));
                  break;
                case 412004:
                  dispatch(setCnilError('invalid'));
                  break;
                case 412408:
                  dispatch(setCnilError('isLogin'));
                  break;
                case 412401:
                  dispatch(setCnilError('tooShort'));
                  break;
                case 412402:
                  dispatch(setCnilError('tooLong'));
                  break;
                case 412409:
                  dispatch(setCnilError('space'));
                  break;
                case 412410:
                  dispatch(setCnilError('accentedChars'));
                  break;
                case 412411:
                  dispatch(setCnilError('specialChars'));
                  break;
                case 412412:
                  dispatch(setInputError({ field, error: 'samePassword' }));
                  break;
                default:
                  dispatch(goToErrorPage());
                  break;
              }
              break;
            case 'passAgain':
              switch (code) {
                case 412002:
                  dispatch(setInputError({ field, error: 'empty' }));
                  break;
                case 412004:
                  dispatch(setInputError({ field, error: 'notEqual' }));
                  break;
                default:
                  dispatch(goToErrorPage());
                  break;
              }
              break;
            default:
              dispatch(goToErrorPage());
              break;
          }
        }
      },
    );
});

/**
 * Function to update the user password with the new given one
 * @param password  user input new password
 * @param passAgain user input new password confirmation
 * @returns {const}
 */
export const resetPassword = ({ password, passAgain }) => ((dispatch, getState) => {
  const { identitiesId } = getState().identification;
  TagService.sendAction('updatePassword');
  return dispatch(doResetPassword({ identitiesId, passAgain, password }));
});
